import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarPlus, Download, Eye } from "lucide-react";
import { ApplyLeaveDialog } from "@/components/leave/ApplyLeaveDialog";
import { LeaveDetailsDialog, StatusBadge, TYPES, TYPE_LABEL, downloadBlob, fmtDays } from "@/components/leave/shared";

const BalanceCard = ({ t, b }) => (
  <div className="flat-card p-5 border-l-4 border-l-[#002FA7]" data-testid={`balance-card-${t}`}>
    <div className="label-eyebrow">{TYPE_LABEL[t]}</div>
    <div className="mt-2 flex items-baseline gap-1">
      <span className="text-4xl font-black tracking-tight" data-testid={`balance-available-${t}`}>{fmtDays(b?.available)}</span>
      <span className="text-sm text-[#4B5563]">/ {fmtDays(b?.total)} credited</span>
    </div>
    <div className="text-[11px] text-[#4B5563] mt-1">
      Taken {fmtDays(b?.taken_this_year)} this year{b?.pending ? ` · ${fmtDays(b.pending)} pending` : ""}{b?.carry_forward ? ` · ${fmtDays(b.carry_forward)} carried forward` : ""}
    </div>
  </div>
);

const ElInfo = ({ s }) => {
  const n = s.el_next;
  const j = s.employee.joining_date;
  let msg = "Joining date not set — please contact HR to enable Earned Leave.";
  if (j && n && !n.eligible) msg = `Earned Leave eligibility starts after completing ${s.el_eligibility_months} months (on ${fmtDate(n.date)}, ${fmtDays(n.days)} days).`;
  else if (j && n) msg = `Next Earned Leave credit: ${fmtDays(n.days)} day on ${fmtDate(n.date)}.`;
  return <span data-testid="el-info">{msg}</span>;
};

export default function MyLeaves() {
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [apps, setApps] = useState([]);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState(params.get("tab") === "team" ? "team" : "mine");
  const [applyOpen, setApplyOpen] = useState(false);
  const [view, setView] = useState(null);
  const [remarks, setRemarks] = useState("");

  const load = async () => {
    try {
      const [r, mine, tm] = await Promise.all([
        api.get("/leave/my/summary"),
        api.get("/leave/my/applications"),
        api.get("/leave/team/applications").catch(() => ({ data: [] })),
      ]);
      setS(r.data);
      setApps(mine.data);
      setTeam(tm.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load leave details"); }
  };
  useEffect(() => { load(); }, []);

  const act = async (url, body, okMsg) => {
    try {
      await api.post(url, body);
      toast.success(okMsg);
      setView(null); setRemarks("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (err) return (<div className="px-4 sm:px-8 py-6 space-y-6"><PageHeader eyebrow="HR & Payroll" title="My Leaves" /><div className="flat-card p-8 text-sm" data-testid="my-leaves-error">{err}</div></div>);
  if (!s) return <div className="p-8 text-sm text-[#9CA3AF]">Loading…</div>;
  const rows = tab === "team" ? team : apps;

  return (
    <div className="px-4 sm:px-8 py-6 space-y-6" data-testid="my-leaves-page">
      <PageHeader eyebrow="HR & Payroll" title="My Leaves"
        subtitle={`${s.employee.name}${s.employee.employee_code ? ` · ${s.employee.employee_code}` : ""} · Leave year ${fmtDate(s.balances.year.start)} – ${fmtDate(s.balances.year.end)}`}
        action={<Button onClick={() => setApplyOpen(true)} data-testid="apply-leave-btn" className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-10"><CalendarPlus className="w-4 h-4 mr-2" /> Apply for Leave</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {TYPES.map((t) => <BalanceCard key={t} t={t} b={s.balances[t]} />)}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Mini label="Taken this leave year" value={fmtDays(s.taken_this_year)} testid="stat-taken" />
        <Mini label="Pending applications" value={s.pending_count} testid="stat-pending" />
        <Mini label="Upcoming approved" value={s.upcoming.length ? s.upcoming.map((u) => `${u.leave_type} ${fmtDate(u.from_date)}`).join(", ") : "None"} testid="stat-upcoming" />
        <Mini label="Joining date" value={s.employee.joining_date ? fmtDate(s.employee.joining_date) : "Not set"} sub={<ElInfo s={s} />} testid="stat-joining" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none">
            <TabsTrigger value="mine" className="rounded-none" data-testid="tab-my-apps">My Applications ({apps.length})</TabsTrigger>
            {s.is_manager && <TabsTrigger value="team" className="rounded-none" data-testid="tab-team-apps">Team Approvals ({team.filter((a) => a.status === "Pending" && a.stage === "Manager").length})</TabsTrigger>}
          </TabsList>
        </Tabs>
        {tab === "mine" && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none text-xs uppercase tracking-widest font-bold h-9" onClick={() => window.print()} data-testid="print-history">Print</Button>
            <Button variant="outline" className="rounded-none text-xs uppercase tracking-widest font-bold h-9" data-testid="download-history"
              onClick={() => downloadBlob("/leave/my/export", "my-leave-history.xlsx").catch(() => toast.error("Download failed"))}><Download className="w-4 h-4 mr-1" /> Excel</Button>
          </div>
        )}
      </div>
      <AppsTable rows={rows} team={tab === "team"} onView={setView} />
      <ApplyLeaveDialog open={applyOpen} onClose={() => setApplyOpen(false)} summary={s} onDone={() => { setApplyOpen(false); load(); }} />
      <LeaveDetailsDialog app={view} onClose={() => { setView(null); setRemarks(""); }}>
        {view && <Actions app={view} team={tab === "team"} remarks={remarks} setRemarks={setRemarks} act={act} />}
      </LeaveDetailsDialog>
    </div>
  );
}

const Mini = ({ label, value, sub, testid }) => (
  <div className="flat-card p-4" data-testid={testid}>
    <div className="label-eyebrow">{label}</div>
    <div className="font-bold text-sm mt-1">{value}</div>
    {sub && <div className="text-[11px] text-[#4B5563] mt-1">{sub}</div>}
  </div>
);

const AppsTable = ({ rows, team, onView }) => (
  <div className="border border-[#E5E7EB] overflow-x-auto bg-white" data-testid="leave-apps-table">
    <table className="w-full text-sm">
      <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
        <tr>{[team ? "Employee" : null, "Applied", "Type", "From", "To", "Days", "Reason", "Status", "Decided By", "Remarks", ""].filter((h) => h !== null).map((h) => <th key={h} className="text-left px-3 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-[#9CA3AF]">No applications yet.</td></tr>}
        {rows.map((a) => (
          <tr key={a.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`leave-row-${a.id}`}>
            {team && <td className="px-3 py-2 font-bold">{a.employee_name}</td>}
            <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.applied_at)}</td>
            <td className="px-3 py-2 font-bold">{a.leave_type}</td>
            <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.from_date)}</td>
            <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.to_date)}</td>
            <td className="px-3 py-2">{fmtDays(a.days)}</td>
            <td className="px-3 py-2 text-xs max-w-[220px] truncate">{a.reason}</td>
            <td className="px-3 py-2"><StatusBadge app={a} /></td>
            <td className="px-3 py-2 text-xs">{a.decided_by || "—"}</td>
            <td className="px-3 py-2 text-xs max-w-[200px] truncate">{a.hr_remarks || (a.clarification_requested ? a.clarification_note : null) || "—"}</td>
            <td className="px-3 py-2"><button onClick={() => onView(a)} className="text-[#002FA7]" data-testid={`leave-view-${a.id}`}><Eye className="w-4 h-4" /></button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Actions = ({ app, team, remarks, setRemarks, act }) => {
  const btn = "rounded-none uppercase tracking-widest text-xs font-bold h-9";
  const base = `/leave/applications/${app.id}`;
  const showRemarks = team ? app.status === "Pending" && app.stage === "Manager"
    : app.clarification_requested || (app.status === "Approved" && !app.cancellation_requested);
  return (
    <div className="mt-4 space-y-2 border-t border-[#E5E7EB] pt-4">
      {showRemarks && <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="rounded-none"
        placeholder={team ? "Remarks (required to reject)" : app.clarification_requested ? "Your response to HR" : "Reason for cancellation"} data-testid="leave-action-remarks" />}
      <div className="flex flex-wrap gap-2">
        {team && app.status === "Pending" && app.stage === "Manager" && (<>
          <Button className={`${btn} bg-[#10B981] hover:bg-[#059669]`} onClick={() => act(`${base}/manager-action`, { action: "approve", remarks }, "Approved — sent to HR")} data-testid="mgr-approve">Approve</Button>
          <Button variant="outline" className={`${btn} text-[#B91C1C] border-[#B91C1C]`} onClick={() => act(`${base}/manager-action`, { action: "reject", remarks }, "Rejected")} data-testid="mgr-reject">Reject</Button>
        </>)}
        {!team && app.status === "Pending" && <Button variant="outline" className={btn} onClick={() => act(`${base}/withdraw`, {}, "Application withdrawn")} data-testid="leave-withdraw">Withdraw</Button>}
        {!team && app.clarification_requested && <Button className={`${btn} bg-[#002FA7]`} onClick={() => act(`${base}/respond`, { remarks }, "Response sent")} data-testid="leave-respond">Send Response</Button>}
        {!team && app.status === "Approved" && !app.cancellation_requested && <Button variant="outline" className={`${btn} text-[#B91C1C] border-[#B91C1C]`} onClick={() => act(`${base}/request-cancel`, { remarks }, "Cancellation requested")} data-testid="leave-request-cancel">Request Cancellation</Button>}
      </div>
    </div>
  );
};
